import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import Icon1 from "@assets/icons/Icon";
import { TrackingContentV2 } from "@screens/TrackingScreenV2";
import type { TrackingData } from "@services/api/tracking";

interface StatusInfo {
  key: string;
  label: string;
  description: string;
  clientSees: string;
  auto?: string;
  conditions?: string[];
}

const STATUS_FLOW: StatusInfo[] = [
  {
    key: "rdv",
    label: "RDV",
    description: "Le rendez-vous vient d'être pris. Les informations (carte grise, interventions, adresse, kilométrage) ne sont pas encore complètes.",
    clientSees: "Page de suivi : dépôt de la carte grise et infos manquantes demandés.",
    auto: "Automatique vers « À préparer » dès que carte grise + interventions + adresse + km sont réunis.",
  },
  {
    key: "a_preparer",
    label: "A préparer",
    description: "Prise de RDV finalisée : carte grise, interventions, adresse et kilométrage sont réunis. Les pièces peuvent être préparées.",
    clientSees: "Pas encore visible sur le suivi client.",
  },
  {
    key: "devis_initial",
    label: "Devis initial",
    description: "Le devis initial a été envoyé au client. En attente de la signature.",
    clientSees: "\"Devis en attente\" avec lien de signature sur la page de suivi.",
    auto: "Automatique à l'envoi du devis initial.",
    conditions: ["Signature client requise pour passer en commande"],
  },
  {
    key: "commande",
    label: "Commande",
    description: "Le devis a été signé, les pièces ou fournitures peuvent être commandées. En attente de livraison.",
    clientSees: "\"En attente de pièces\" sur la page de suivi.",
    auto: "Automatique quand le devis initial est signé par le client.",
  },
  {
    key: "pret",
    label: "Prêt",
    description: "Tout le matériel est disponible. Le véhicule peut être réceptionné et l'intervention planifiée.",
    clientSees: "\"En attente du véhicule\" sur la page de suivi.",
  },
  {
    key: "reception",
    label: "Réceptionné",
    description: "Le véhicule a été déposé au garage. L'intervention peut commencer.",
    clientSees: "\"Véhicule déposé\" sur la page de suivi.",
  },
  {
    key: "en_cours",
    label: "En cours",
    description: "Les travaux sont en cours. Si des travaux supplémentaires sont découverts, un devis additionnel peut être envoyé.",
    clientSees: "\"Réparations en cours\" sur la page de suivi.",
  },
  {
    key: "devis_supp",
    label: "Devis supp.",
    description: "Un devis supplémentaire a été envoyé au client suite à des travaux additionnels découverts après réception du véhicule.",
    clientSees: "\"Devis supplémentaire\" avec lien de signature sur la page de suivi.",
    auto: "Automatique à l'envoi d'un devis additionnel après réception.",
    conditions: ["Signature client requise pour reprendre les travaux"],
  },
  {
    key: "accord",
    label: "Accord",
    description: "Le client a accepté et signé le devis supplémentaire. Les travaux peuvent reprendre.",
    clientSees: "\"Réparations en cours\" sur la page de suivi.",
    auto: "Automatique quand le devis supplémentaire est signé par le client.",
  },
  {
    key: "acompte",
    label: "Acompte",
    description: "Un acompte a été demandé au client. En attente du paiement.",
    clientSees: "\"Acompte en attente\" avec lien de paiement sur la page de suivi.",
    auto: "Automatique à la demande d'acompte. Repasse en « En cours » quand l'acompte est payé.",
  },
  {
    key: "fin_de_travaux",
    label: "Fin de travaux",
    description: "L'intervention est terminée. La facturation peut être lancée.",
    clientSees: "\"Travaux terminés\" sur la page de suivi.",
  },
  {
    key: "facturation",
    label: "Facturation",
    description: "La facture a été émise. Le solde peut maintenant être demandé au client.",
    clientSees: "\"Facture disponible\" avec lien pour consulter la facture sur la page de suivi.",
  },
  {
    key: "solde",
    label: "Solde",
    description: "Le paiement du solde a été demandé au client (envoi de la facture). En attente du règlement.",
    clientSees: "\"Paiement en attente\" avec lien de paiement sur la page de suivi.",
    auto: "Automatique à l'envoi de la facture / demande de solde.",
  },
  {
    key: "solde_paye",
    label: "Solde payé",
    description: "Le solde a été réglé. Le véhicule est prêt à être récupéré.",
    clientSees: "\"Paiement validé\" sur la page de suivi.",
    auto: "Automatique quand le solde est payé.",
  },
  {
    key: "disponible",
    label: "Disponible",
    description: "Le véhicule est prêt à être récupéré par le client.",
    clientSees: "\"Véhicule disponible\" sur la page de suivi. Notification par message/WhatsApp.",
  },
  {
    key: "restitue",
    label: "Restitué",
    description: "Le véhicule a été rendu au client. L'ordre est clôturé.",
    clientSees: "\"Véhicule restitué\" + formulaire d'évaluation.",
  },
  {
    key: "arrete",
    label: "Arrêté",
    description: "Dossier suspendu ou arrêté côté ServiceBox (état anormal, hors du cycle normal). Importé tel quel depuis ServiceBox.",
    clientSees: "Pas de changement particulier sur la page de suivi.",
  },
  {
    key: "annule",
    label: "Annulé",
    description: "Rendez-vous annulé : posé à la main, ou automatiquement quand un devis n'a pas été validé dans les délais. L'ordre reste dans le dossier comme trace, mais il ne prend plus de place dans les plannings (réception, restitution, atelier, véhicule de prêt).",
    clientSees: "Message d'annulation envoyé par e-mail et, si possible, par WhatsApp.",
  },
];

/** Base mock data shared across all previews */
const BASE_MOCK: TrackingData = {
  ordre_num: "1234",
  org_name: "Garage Martin",
  org_address: "12 rue de la Paix, 75002 Paris",
  org_phone: "01 23 45 67 89",
  org_email: "contact@garage-martin.fr",
  org_whatsapp_phone: "+33123456789",
  tracking_step: "en_attente_devis",
  has_carte_grise: true,
  vehicle_plate: "AB-123-CD",
  service_type: "Mécanique",
  intervention_type: "Entretien",
  interventions: ["Vidange", "Freins"],
  deposit_date: "07/04/2026",
  restitution_date: "09/04/2026",
  devis_requested: false,
  devis_signed: false,
  devis_supplementaire_requested: false,
  acompte_requested: false,
  acompte_paid: false,
  solde_requested: false,
  solde_paid: false,
  facture_available: false,
};

/**
 * Build a mock TrackingData that makes TrackingContentV2 display exactly
 * what the client sees at this internal status.
 */
function buildMockData(statusKey: string): TrackingData | null {
  switch (statusKey) {
    case "rdv":
      return {
        ...BASE_MOCK,
        tracking_step: "carte_grise_manquante",
        has_carte_grise: false,
        devis_requested: false,
      };
    case "a_preparer":
      return {
        ...BASE_MOCK,
        tracking_step: "carte_grise_manquante",
        has_carte_grise: false,
        devis_requested: false,
      };
    case "devis_initial":
      return {
        ...BASE_MOCK,
        tracking_step: "devis_pret",
        devis_requested: true,
        devis_sign_token: "fake-token",
      };
    case "commande":
      return {
        ...BASE_MOCK,
        tracking_step: "en_attente_depot",
        devis_requested: true,
        devis_signed: true,
        initial_devis_signed: true,
      };
    case "pret":
      return {
        ...BASE_MOCK,
        tracking_step: "en_attente_depot",
        devis_requested: true,
        devis_signed: true,
        initial_devis_signed: true,
      };
    case "reception":
      return {
        ...BASE_MOCK,
        tracking_step: "vehicule_depose",
        devis_requested: true,
        devis_signed: true,
        initial_devis_signed: true,
      };
    case "en_cours":
      return {
        ...BASE_MOCK,
        tracking_step: "reparations_en_cours",
        devis_requested: true,
        devis_signed: true,
        initial_devis_signed: true,
      };
    case "devis_supp":
      return {
        ...BASE_MOCK,
        tracking_step: "devis_supplementaire",
        devis_requested: true,
        devis_signed: true,
        initial_devis_signed: true,
        devis_supplementaire_requested: true,
        devis_sign_token: "fake-token-supp",
      };
    case "accord":
      return {
        ...BASE_MOCK,
        tracking_step: "reparations_en_cours",
        devis_requested: true,
        devis_signed: true,
        initial_devis_signed: true,
        devis_supplementaire_requested: true,
        additional_devis_signed: true,
      };
    case "acompte":
      return {
        ...BASE_MOCK,
        tracking_step: "acompte_manquant",
        devis_requested: true,
        devis_signed: true,
        initial_devis_signed: true,
        acompte_requested: true,
        payment_link_url: "https://example.com/pay",
      };
    case "fin_de_travaux":
      return {
        ...BASE_MOCK,
        tracking_step: "travaux_termines",
        devis_requested: true,
        devis_signed: true,
        initial_devis_signed: true,
      };
    case "facturation":
      return {
        ...BASE_MOCK,
        tracking_step: "facture_disponible",
        devis_requested: true,
        devis_signed: true,
        initial_devis_signed: true,
        facture_available: true,
        facture_pdf_url: "https://example.com/facture.pdf",
      };
    case "solde":
      return {
        ...BASE_MOCK,
        tracking_step: "solde_en_attente",
        devis_requested: true,
        devis_signed: true,
        initial_devis_signed: true,
        facture_available: true,
        facture_pdf_url: "https://example.com/facture.pdf",
        solde_requested: true,
        payment_link_url: "https://example.com/pay",
      };
    case "solde_paye":
      return {
        ...BASE_MOCK,
        tracking_step: "paiement_valide",
        devis_requested: true,
        devis_signed: true,
        initial_devis_signed: true,
        facture_available: true,
        solde_requested: true,
        solde_paid: true,
      };
    case "disponible":
      return {
        ...BASE_MOCK,
        tracking_step: "vehicule_disponible",
        devis_requested: true,
        devis_signed: true,
        initial_devis_signed: true,
        facture_available: true,
        solde_requested: true,
        solde_paid: true,
      };
    case "restitue":
      return {
        ...BASE_MOCK,
        tracking_step: "vehicule_restitue",
        devis_requested: true,
        devis_signed: true,
        initial_devis_signed: true,
        facture_available: true,
        solde_requested: true,
        solde_paid: true,
        client_rating: 4,
      };
    default:
      return null;
  }
}

const LIST_W = 440;
const PREVIEW_W = 340;
const GAP = 12;
const TOTAL_W = LIST_W + GAP + PREVIEW_W;

export default function OrdreStatusGuide() {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [previewHoveredStar, setPreviewHoveredStar] = useState(0);
  const [previewSelectedRating, setPreviewSelectedRating] = useState(0);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (
        panelRef.current && !panelRef.current.contains(e.target as Node) &&
        triggerRef.current && !triggerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  useEffect(() => {
    if (!open || !triggerRef.current) {
      setPos(null);
      return;
    }
    const rect = triggerRef.current.getBoundingClientRect();
    requestAnimationFrame(() => {
      const panelH = panelRef.current?.offsetHeight || 600;
      const top = rect.bottom + 8 + panelH > window.innerHeight
        ? Math.max(8, rect.top - panelH - 8)
        : rect.bottom + 8;
      const left = Math.min(rect.left, window.innerWidth - TOTAL_W - 16);
      setPos({ top, left: Math.max(8, left) });
    });
  }, [open]);

  useEffect(() => {
    if (!open) setHoveredIndex(null);
  }, [open]);

  const mockData = hoveredIndex !== null ? buildMockData(STATUS_FLOW[hoveredIndex].key) : null;

  // Reset rating state when switching preview
  useEffect(() => {
    if (mockData?.client_rating) {
      setPreviewSelectedRating(mockData.client_rating);
    } else {
      setPreviewSelectedRating(0);
    }
    setPreviewHoveredStar(0);
  }, [hoveredIndex]);

  return (
    <>
      <button
        ref={triggerRef}
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 rounded-lg transition-colors"
        style={{
          padding: "6px 12px",
          background: open ? "#F5F5F5" : "transparent",
          border: "1px solid #E5E5E5",
        }}
        onMouseEnter={(e) => { if (!open) (e.currentTarget.style.background = "#FAFAFA"); }}
        onMouseLeave={(e) => { if (!open) (e.currentTarget.style.background = "transparent"); }}
      >
        <Icon1 name="question" size={14} color={open ? "#404040" : "#A1A1A1"} />
        <span style={{ fontFamily: "Poppins", fontSize: 12, fontWeight: 500, color: "#737373" }}>
          Guide des statuts
        </span>
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          className="flex"
          style={{
            position: "fixed",
            zIndex: 10001, // above BaseModal (z-[10000])
            top: pos?.top ?? -9999,
            left: pos?.left ?? -9999,
            opacity: pos ? 1 : 0,
            gap: GAP,
            alignItems: "flex-start",
          }}
        >
          {/* Left: status list */}
          <div
            style={{
              width: LIST_W,
              maxHeight: "85vh",
              overflowY: "auto",
              background: "#FFFFFF",
              borderRadius: 12,
              boxShadow: "0 8px 30px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.06)",
            }}
          >
            {/* Header */}
            <div
              style={{
                padding: "20px 24px 16px",
                borderBottom: "1px solid #EBEBEB",
                position: "sticky",
                top: 0,
                background: "#FFFFFF",
                borderRadius: "12px 12px 0 0",
                zIndex: 1,
              }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 15, color: "#1A1A1A", display: "block" }}>
                    Cycle de vie d'un ordre
                  </span>
                  <span style={{ fontFamily: "Poppins", fontSize: 11, color: "#999" }}>
                    {STATUS_FLOW.length} étapes — survolez pour prévisualiser
                  </span>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-center rounded-full hover:bg-neutral-100"
                  style={{ width: 28, height: 28 }}
                >
                  <Icon1 name="close" size={10} color="#999" />
                </button>
              </div>
            </div>

            {/* Status list */}
            <div style={{ padding: "4px 12px 12px" }}>
              {STATUS_FLOW.map((s, i) => {
                const isHovered = hoveredIndex === i;
                return (
                  <div
                    key={s.key}
                    className="flex"
                    style={{
                      minHeight: 80,
                      borderRadius: 8,
                      background: isHovered ? "#F8F9FF" : "transparent",
                      marginLeft: -4,
                      marginRight: -4,
                      paddingLeft: 4,
                      paddingRight: 4,
                      cursor: "default",
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={() => setHoveredIndex(i)}
                    onMouseLeave={() => setHoveredIndex(null)}
                  >
                    {/* Timeline column */}
                    <div className="flex flex-col items-center" style={{ width: 28, flexShrink: 0 }}>
                      <div style={{ width: 1, height: 16, background: i === 0 ? "transparent" : "#E0E0E0" }} />
                      <div
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          background: isHovered ? "#5C6BFF" : (i === STATUS_FLOW.length - 1 ? "#1A1A1A" : "#FFFFFF"),
                          border: `2px solid ${isHovered ? "#5C6BFF" : "#1A1A1A"}`,
                          flexShrink: 0,
                          transition: "all 0.15s",
                        }}
                      />
                      <div style={{ width: 1, flex: 1, background: i === STATUS_FLOW.length - 1 ? "transparent" : "#E0E0E0" }} />
                    </div>

                    {/* Content */}
                    <div style={{ flex: 1, padding: "6px 0 16px 12px" }}>
                      <div className="flex items-center" style={{ gap: 8, marginBottom: 6 }}>
                        <span
                          style={{
                            fontFamily: "Poppins",
                            fontWeight: 600,
                            fontSize: 13,
                            color: isHovered ? "#5C6BFF" : "#1A1A1A",
                            transition: "color 0.15s",
                          }}
                        >
                          {s.label}
                        </span>
                        <span style={{ fontFamily: "Poppins", fontSize: 10, color: "#CCC", fontWeight: 500 }}>
                          {i + 1}/{STATUS_FLOW.length}
                        </span>
                      </div>

                      <p style={{ fontFamily: "Poppins", fontSize: 12, lineHeight: "18px", color: "#666", margin: "0 0 6px" }}>
                        {s.description}
                      </p>

                      <div className="flex items-start" style={{ gap: 6, marginBottom: (s.auto || s.conditions?.length) ? 6 : 0 }}>
                        <span style={{ marginTop: 2, flexShrink: 0 }}><Icon1 name="eye" size={12} color="#999" /></span>
                        <span style={{ fontFamily: "Poppins", fontSize: 11, color: "#999", lineHeight: "16px" }}>
                          {s.clientSees}
                        </span>
                      </div>

                      {s.auto && (
                        <div className="flex items-start" style={{ gap: 6, marginBottom: s.conditions?.length ? 6 : 0 }}>
                          <svg width="12" height="12" viewBox="0 0 12 12" style={{ marginTop: 2, flexShrink: 0 }}>
                            <path d="M3 6L5.5 8.5L9 3.5" stroke="#1A1A1A" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          <span style={{ fontFamily: "Poppins", fontSize: 11, color: "#666", lineHeight: "16px" }}>
                            {s.auto}
                          </span>
                        </div>
                      )}

                      {s.conditions && s.conditions.length > 0 && (
                        <div className="flex flex-wrap" style={{ gap: 4 }}>
                          {s.conditions.map((c) => (
                            <span
                              key={c}
                              style={{
                                fontFamily: "Poppins",
                                fontSize: 10,
                                fontWeight: 500,
                                color: "#666",
                                background: "#F5F5F5",
                                border: "1px solid #E5E5E5",
                                padding: "2px 8px",
                                borderRadius: 4,
                              }}
                            >
                              {c}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div
              style={{
                padding: "12px 24px 16px",
                borderTop: "1px solid #EBEBEB",
                position: "sticky",
                bottom: 0,
                background: "#FFFFFF",
                borderRadius: "0 0 12px 12px",
              }}
            >
              <p style={{ fontFamily: "Poppins", fontSize: 11, color: "#999", margin: 0, lineHeight: "16px" }}>
                Les statuts peuvent être changés manuellement. Certaines transitions sont automatiques lors de la signature du devis ou du paiement. Le devis doit être signé avant de commander, et la facture émise avant de demander le solde.
              </p>
            </div>
          </div>

          {/* Right: live tracking preview */}
          <div
            style={{
              width: PREVIEW_W,
              maxHeight: "85vh",
              background: "#FFFFFF",
              borderRadius: 12,
              boxShadow: "0 8px 30px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.06)",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Preview header */}
            <div
              style={{
                padding: "12px 14px 10px",
                borderBottom: "1px solid #EBEBEB",
                background: "#F8F9FF",
                flexShrink: 0,
              }}
            >
              <div className="flex items-center" style={{ gap: 6 }}>
                <Icon1 name="eye" size={12} color="#5C6BFF" />
                <span style={{ fontFamily: "Poppins", fontSize: 11, fontWeight: 600, color: "#5C6BFF" }}>
                  Vue client
                </span>
              </div>
              <span style={{ fontFamily: "Poppins", fontSize: 10, color: "#9CA3AF", marginTop: 2, display: "block" }}>
                Page de suivi telle que le client la voit
              </span>
            </div>

            {/* Scaled tracking content */}
            <div
              style={{
                flex: 1,
                overflowY: "auto",
                overflowX: "hidden",
              }}
            >
              {mockData ? (
                <div
                  style={{
                    width: 420,
                    transform: `scale(${PREVIEW_W / 420})`,
                    transformOrigin: "top left",
                    pointerEvents: "none",
                  }}
                >
                  <div className="bg-[#F8F9FF]">
                    <TrackingContentV2
                      data={mockData}
                      hoveredStar={previewHoveredStar}
                      setHoveredStar={setPreviewHoveredStar}
                      selectedRating={previewSelectedRating}
                      setSelectedRating={setPreviewSelectedRating}
                      previewMode
                    />
                  </div>
                </div>
              ) : (
                /* Nothing hovered */
                <div className="flex flex-col items-center justify-center" style={{ height: 280, padding: 24 }}>
                  <Icon1 name="cursor" size={20} color="#D1D5DB" />
                  <span style={{ fontFamily: "Poppins", fontSize: 11, color: "#9CA3AF", textAlign: "center", marginTop: 8 }}>
                    Survolez un statut pour voir ce que le client voit
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
